import React, { useRef, useState } from 'react';
import { ProductSummaryType } from '@adac-mp/shared';
import { Drawer, Modal, Typography } from '../../base-components';
import { ClickEvent, TrackingProps } from '../../utils/tracking/types';
import { trackButtonClick } from '../../utils/tracking/tracking';

interface IProductSummaryElementProps {
	['data-cy']?: string;
	id?: string;
	type?: ProductSummaryType;
	role?: 'button';
	wrap?: boolean;
	label?: string;
	labelColor?: 'title' | 'lightText';
	value?: React.ReactNode;
	hidden?: boolean;
	wide?: boolean;
	tracking?: ClickEvent;
	notIndented?: boolean;
	hasErrors?: boolean;
	icon?: React.ReactNode;
	submitButtonLabel?: string;
	submitTracking?: TrackingProps;
	cancelButtonLabel?: string;
	children?: React.ReactNode;
	className?: string;
	onChange?: (modalParentRef?: React.MutableRefObject<HTMLElement | null>) => void;
	onClose?: (modalParentRef?: React.MutableRefObject<HTMLElement | null>) => void;
	onClick?: () => void;
	editable?: boolean;
}

// eslint-disable-next-line complexity
export const ProductSummaryElement = ({
	'data-cy': dataCy,
	wrap = false,
	id,
	type,
	role,
	label,
	labelColor = 'title',
	value,
	editable = true,
	icon,
	tracking,
	hidden = false,
	wide = false,
	hasErrors = false,
	notIndented = false,
	submitButtonLabel,
	submitTracking,
	cancelButtonLabel,
	children,
	className,
	onChange = () => {},
	onClose = () => {},
	onClick,
}: IProductSummaryElementProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const modalParentRef = useRef(null);

	const closeModalHandler = () => {
		setIsOpen(false);
		onClose(modalParentRef);
	};

	const submitHandler = () => {
		onChange(modalParentRef);
		setIsOpen(false);
	};

	const submitWithoutClosingModal = () => {
		onChange(modalParentRef);
	};

	return (
		<>
			<div
				ref={modalParentRef}
				data-cy={dataCy}
				className={`${
					type || role === 'button'
						? ' flex-grow items-end cursor-pointer hover:bg-[#FAFAFA]'
						: 'cursor-default'
				} group flex justify-between bg-white ${
					notIndented ? '' : 'px-4'
				} py-2.5 border-solid border-l-0 border-b-0 border-r-0 border-t border-t-gray-600 last:rounded-b transition ease-in-out duration-300 focus-visible:outline-primary ${className}`}
				role={role}
				tabIndex={role === 'button' ? 0 : -1}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						e.preventDefault();
						if (!editable) return;

						if (onClick) {
							onClick();
						} else {
							setIsOpen(true);
						}

						if (tracking) trackButtonClick(tracking);
					}
				}}
				onClick={() => {
					if (!editable) return;

					if (onClick) {
						onClick();
					} else {
						setIsOpen(true);
					}

					if (tracking) trackButtonClick(tracking);
				}}>
				{value ? (
					<Typography
						data-cy={`${dataCy}-description`}
						className={`w-full flex ${wrap ? 'items-baseline' : 'items-center'} min-w-0`}
						component="div"
						size="lgEven"
						color="lightText">
						{type !== 'cart' && label && <span className="whitespace-nowrap">{label}:</span>}
						<Typography
							className={`w-full pl-1 ${wrap ? '' : 'whitespace-nowrap overflow-hidden text-ellipsis'}`}
							size="lgEven"
							color="title">
							{value}
						</Typography>
					</Typography>
				) : (
					<Typography component="p" size="lgEven" color={labelColor}>
						{label}
					</Typography>
				)}
				{icon && editable && (
					<span className="opacity-80 transition-opacity shrink-0 group-hover:opacity-100">{icon}</span>
				)}
			</div>
			{type === 'info' && (
				<Drawer
					title={value ? `${label}: ${value}` : label}
					paddingX="small"
					isOpen={isOpen}
					setIsOpen={setIsOpen}>
					{children}
				</Drawer>
			)}
			{(type === 'edit' || type === 'cart') && (
				<Modal
					withSubmissionButtons
					fit={id === 'dates'}
					style={{ overflow: 'unset' }}
					hidden={hidden}
					wide={wide}
					isOpen={isOpen}
					title={label}
					trackingData={submitTracking}
					submitButtonLabel={submitButtonLabel}
					cancelButtonLabel={cancelButtonLabel}
					onClose={closeModalHandler}
					onSubmit={hasErrors ? submitWithoutClosingModal : submitHandler}>
					{children}
				</Modal>
			)}
		</>
	);
};
